import { AuditStatus, EntityModules, ProtectorType } from 'core/constants';
import { FormSettingsResponse } from 'stores/FormBuilderSettings/types';
import { LocationAnswerType, UuidableName } from 'utils/api/types';
import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';
import { RuleOperator } from 'utils/api/modules/memberGroups/memberGroups.types';
import { ControlStatus } from 'containers/Controls/constants';

import { AnswerTypes, JSONSchemaType } from '../incidents/incidents.types';

export interface ShareDocumentLinkResponse {
  uuid: string;
  createdAt: string;
  createdBy: UuidableName;
  isActive: boolean;
}

export type FormBuilderPropertiesType = {
  [key: string]: FormBuilderProperties;
};

/** `colour` is an audits option-colour key (`colors.ui.optionColour`); Events options never carry one. */
export type EnumOptions = { key: string; value: string; colour?: string | null }[];

export type ImageType = {
  uuid: string;
  file?: File | string;
};

export type EntitiesModulesUpdateKeys = {
  site: string | null;
  asset: string | null;
  contact: string | null;
  risk: string | null;
  event: string | null;
  document: string | null;
};

export type FormBuilderProperties = {
  actions: FormActionsType;
  questionType?: ProtectorType;
  lookupType?: string;
  order: string[];
  title: string;
  description?: string;
  required: string[];
  properties: FormBuilderPropertiesType;
  enum?: EnumOptions;
  image?: ImageType;
  roles?: EntityFieldRoles[];
  /** audits lookup questions only: the answer is a list of record uuids */
  multiple?: boolean;
};

export type FormBuilderType = {
  order: string[];
  properties: FormBuilderPropertiesType;
};

export type FormActionType = {
  when:
    | {
        // [questionKey, [questionValue]];
        equals: [string, [string]];
      }
    | boolean;
  then: { nextSection: string } | { submit: true };
};

export type FormActionsType = Array<FormActionType>;

export type EntityFormType = {
  uuid: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  rulesUpdatedAt: string;
  form: FormBuilderType;
  settings: FormSettingsResponse;
};

export type PublicFormsLinkResponse = {
  organisationPublicUuid: string;
};

// PATCH payload: `settings` is patched field-by-field, while the response type keeps the full object.
export type EntityFormUpdateRequest = Partial<Omit<EntityFormType, 'settings'>> & {
  settings?: Partial<FormSettingsResponse>;
};

export type FormToFieldMappingResponse = { question: string; field: string | null }[];

export type ValueType =
  | string
  | number
  | string[]
  | UuidableName
  | LocationAnswerType
  | { key: string; value: string }
  | { key: string; value: string }[]
  | null;

export type EntityCountType = {
  count: number;
};

export type EntityHistoryResponseType = {
  entityField: {
    key: string;
    /** null on synthetic rows that are not field edits, such as a risk's control events. */
    uuid: string | null;
  };
  current: ValueType;
  previous: ValueType;
  createdAt: string;
  createdBy: UuidableName | null;
};

export type EntityFieldType = {
  uuid: string;
  key: string;
  name: string;
  protectorType: ProtectorType;
  properties?: {
    enum?: EntityEnumsOptions[];
    lookupType?: EntityModules;
    roles?: EntityFieldRoles[];
    required?: boolean;
  };
  internalNote?: string | null;
  helpText?: string | null;
  dataType: string;
  editable: boolean;
  removable: boolean;
  readonly: boolean;
  // field required in layout
  required: boolean;
  // value required
  nullable: boolean;
  deletedAt?: string;
};

export type EntityPropertiesType = {
  [key: string]: {
    type: string;
    order: string[];
    title: string;
    description: string;
  };
};

export type LockedField = {
  message: string;
};

export type EntityLayoutResponse = {
  layout: EntityLayoutType;
  createdAt: string;
  lockedFields?: Record<string, LockedField>;
};

export type EntityLayoutType = {
  type: string;
  order: string[];
  properties: EntityPropertiesType;
};

export type EntityValueType = string | number | null | {} | UuidableName | EntityEnumsOptions[];

/**
 * Conditions on a relevant field at a pipeline stage.
 */
export type FieldConditionOperator = RuleOperator;

export interface FieldCondition {
  /** The trigger EntityField's key. */
  attribute: string;
  operator: FieldConditionOperator;
  /** Option keys, uuids or typed text — always strings on the wire, never {uuid, name}. */
  values: string[];
}

export interface FieldConditionRule {
  combinator: 'all' | 'any';
  conditions: FieldCondition[];
}

/** One relevant field on a pipeline stage, with the rule that decides whether it applies. */
export interface RelevantFieldType {
  key: string;
  required: boolean;
  conditions?: FieldConditionRule;
}

export type EntityType = {
  [key: string]: {
    schema: JSONSchemaType;
    value: EntityValueType;
  };
};

export type EntityDetailsType = {
  layout: EntityLayoutType;
  entity: EntityType;
  createdAt: string;
  createdBy: UuidableName | null;
  owner?: UuidableName | null;
  uuid: string;
  related: EntityModules[];
  relatedActions: EntityRelatedActionsType;
  collaborators: UuidableName[] | null;
  lastSavedAt?: string | null;
  lastSavedBy?: { uuid: string; name: string } | null;
  accessLevel?: DocumentAccessLevel | null;
  isOwner?: boolean | null;
  defaultAccess?: DefaultDocumentAccess | null;
  /** The unit the record belongs to. Absent unless the organisation has scoped permissions. */
  scopeUnit?: string | null;
  archivedAt?: string | null;
  /** Library records only: true while something (a risk, for a control) keeps it from being deleted. */
  inUse?: boolean;
};

export enum EntityFieldRoles {
  ID = 'id',
  STAGE = 'stage',
  DISPLAY = 'display',
  OWNER = 'owner',
  TYPE = 'type',
  NO_FUTURE_DATE = 'no_future_date',
  LAST_RELATED_AUDIT_DATE = 'last_related_audit_date',
  NEXT_AUDIT_DATE = 'next_audit_date',
}

// A date field with one of these holds an instant, so it reads as the reader's day, not as entered
const MOMENT_DATE_ROLES: ReadonlySet<EntityFieldRoles> = new Set([
  EntityFieldRoles.LAST_RELATED_AUDIT_DATE,
  EntityFieldRoles.NEXT_AUDIT_DATE,
]);

export const holdsMomentDate = (protectorType: ProtectorType, roles?: EntityFieldRoles[]): boolean =>
  protectorType === ProtectorType.Date && !!roles?.some((role) => MOMENT_DATE_ROLES.has(role));

export type EntityModuleValues = `${EntityModules}`;

export type ExtraParamsForCreatingEntity = { [key in EntityModuleValues]?: string };

export type EntityEnumsOptions = {
  key: string;
  value: string;
  active: boolean;
  closed: boolean | null;
};

export type EntityRelatedActionsType = {
  total: number;
  completed: number;
};

export type EntityRelatedActionsTypeValue = {
  value: EntityRelatedActionsType;
};

export type AssessmentResponse = {
  uuid: string;
  createdAt: string;
  updatedAt: string;
  assessment: {
    hazard: string;
    harmed: string;
    existingControls: string;
    additionalControls: string;
    likelihoodInitial: number;
    severityInitial: number;
    riskScoreInitial: number;
    likelihoodResidual: number;
    severityResidual: number;
    riskScoreResidual: number;
    actionBy: string;
  };
};

export enum OPERATORS {
  is_any_of = 'is_any_of',
  is_none_of = 'is_none_of',
  submission_form = 'submission_form',
}

type AssignmentConditionValueType = { key: string; value: string }[] | { uuid: string; name: string }[];

type AssignmentConditionType = {
  [key: string]: [string, AssignmentConditionValueType];
};

interface AssignmentRuleBaseType {
  uuid: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  conditions: AssignmentConditionType[];
}

export interface AssignmentRuleResponseType extends AssignmentRuleBaseType {
  targetUsers: {
    uuid: string;
    name: string;
  }[];
  targetExpressions?: [string, string][];
  targetGroups?: {
    uuid: string;
    name: string;
    memberCount: number;
  }[];
}

export interface AssignmentRuleRequestType extends AssignmentRuleBaseType {
  targetUsers: string[];
  targetExpressions?: [string, string][];
  targetGroups?: string[];
}

export interface AssignmentRulePipelineResponseType extends AssignmentRuleBaseType {
  pipeline: {
    uuid: string;
    name: string;
  };
}

export interface AssignmentRulePipelineRequestType extends AssignmentRuleBaseType {
  pipeline: string;
}

export type FolderType = {
  uuid: string;
  name: string;
  order: number;
  parent: {
    uuid: string;
    name: string;
  } | null;
  subfoldersCount: number;
  documentsCount: number;
  subfolders: FolderType[];
  documents: UuidableName[];
};

export type FoldersResponse = {
  folders: FolderType[];
  totalDocuments: number;
};

// Document Permissions

export enum DocumentAccessLevel {
  View = 'view',
  Edit = 'edit',
}

export enum DefaultDocumentAccess {
  AllEdit = 'all_edit',
  AllView = 'all_view',
  Restricted = 'restricted',
}

export type DocumentTokenResponse = {
  token: string;
  accessLevel: DocumentAccessLevel;
  isOwner: boolean;
  imageUploadToken: string | null;
};

export enum DocumentPermissionKind {
  User = 'user',
  Group = 'group',
}

export type DocumentPermissionResponse =
  | {
      uuid: string;
      kind: DocumentPermissionKind.User;
      user: UuidableName;
      accessLevel: DocumentAccessLevel;
      createdAt: string;
      createdBy: UuidableName;
    }
  | {
      uuid: string;
      kind: DocumentPermissionKind.Group;
      group: UuidableName & { memberCount: number };
      accessLevel: DocumentAccessLevel;
      createdAt: string;
      createdBy: UuidableName;
    };

export type DocumentPermissionRequest =
  | { kind: DocumentPermissionKind.User; userUuid: string; accessLevel: DocumentAccessLevel }
  | { kind: DocumentPermissionKind.Group; groupUuid: string; accessLevel: DocumentAccessLevel };

export type PreviewDocumentPermissionLicenceCoverageBody = { groupUuids: string[] };

export type PreviewDocumentPermissionLicenceCoverageResponse = {
  total: number;
  licensed: number;
  skipped: SkippedRecipient[];
};

export type DocumentOwnerResponse = {
  owner: UuidableName;
};

// Document Acknowledgement Requests

export enum AcknowledgementRequestStatus {
  Active = 'active',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum AcknowledgementState {
  Pending = 'pending',
  Acknowledged = 'acknowledged',
  Cancelled = 'cancelled',
}

export type AcknowledgementRequestTotals = {
  total: number;
  acknowledged: number;
  pending: number;
  cancelled: number;
};

export type AcknowledgementRequestSummary = {
  uuid: string;
  name: string;
  document: UuidableName;
  owner: UuidableName;
  dueDate: string;
  status: AcknowledgementRequestStatus;
  totals: AcknowledgementRequestTotals;
  completionPercent: number;
  createdAt: string;
  updatedAt: string;
};

export type AcknowledgementRequestDetail = AcknowledgementRequestSummary & {
  cancelledAt: string | null;
  cancelledBy: UuidableName | null;
  completedAt: string | null;
};

export type AcknowledgementRow = {
  uuid: string;
  recipient: { uuid: string; name: string; email: string };
  state: AcknowledgementState;
  acknowledgedAt: string | null;
  tiptapVersion: number | null;
};

// The signed-in user's own acknowledgement row. Distinct from the owner-side AcknowledgementRow
// (recipient/email-shaped): this nests the document name and parent request, and `cancelled` is
// never returned here.
export type MyAcknowledgementRow = {
  uuid: string;
  state: AcknowledgementState;
  document: { uuid: string; name: string };
  request: {
    uuid: string;
    name: string;
    dueDate: string;
    owner: { uuid: string; name: string };
  };
  acknowledgedAt: string | null;
  tiptapVersion: number | null;
};

export type ListAcknowledgementRequestsParams = {
  status?: string;
  document?: string;
  owner?: string;
  name?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export type ListAcknowledgementsParams = {
  state?: string;
  query?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export type ListMyAcknowledgementsParams = {
  state?: string;
  query?: string;
  // `start::end` ms-timestamp ranges, matching the grid's dueDate filter format.
  dueDate?: string;
  acknowledgedAt?: string;
  time_zone?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
};

export enum SkippedReason {
  Inactive = 'inactive',
  NoDocumentsLicence = 'no_documents_licence',
  NoEventsLicence = 'no_events_licence',
  NoWorkflowsLicence = 'no_workflows_licence',
}

// `reason` widens to string so a token the backend adds later renders verbatim instead of blank.
export type SkippedRecipient = { uuid: string; name: string; reason: SkippedReason | string };

export type AcknowledgementRequestCreateResponse = AcknowledgementRequestSummary & {
  skipped: SkippedRecipient[];
};

export type CreateAcknowledgementRequestBody = {
  name: string;
  document: string;
  dueDate: string;
  recipients: string[];
  recipientGroups?: string[];
};

export type PreviewRecipientsBody = {
  recipients: string[];
  recipientGroups?: string[];
};

// `total` is the deduped, active-and-licensed recipient count the send would actually create.
export type PreviewRecipientsResponse = {
  total: number;
  skipped: SkippedRecipient[];
};

export type PreviewAssignmentRuleTargetGroupsBody = {
  targetGroups: string[];
};

export type PreviewAssignmentRuleTargetGroupsResponse = {
  total: number;
  licensed: number;
  skipped: SkippedRecipient[];
};

export type SetDocumentTiptapVersionResponse = {
  tiptapLastPublishedVersion: number;
};

// Recipient acknowledgement flow (Phase 4) — camelCase wire shape (Phase 3 Findings #3).

export type AcknowledgementOpenRequest = {
  uuid: string;
  name: string;
  dueDate: string;
  owner: { uuid: string; name: string };
};

export type AcknowledgementOpenDocument = { uuid: string; name: string; tiptapVersion: number };

export type AcknowledgementPendingResponse = {
  uuid: string;
  state: AcknowledgementState.Pending;
  request: AcknowledgementOpenRequest;
  document: AcknowledgementOpenDocument;
  tiptapToken: string;
  recipient: { uuid: string; name: string };
  prefilledName: string;
};

export type AcknowledgementReceiptResponse = {
  uuid: string;
  state: AcknowledgementState.Acknowledged;
  request: AcknowledgementOpenRequest;
  document: AcknowledgementOpenDocument;
  acknowledgedAt: string;
  typedName: string;
  signature: string;
};

export type AcknowledgementOpenResponse = AcknowledgementPendingResponse | AcknowledgementReceiptResponse;

export type SubmitAcknowledgementBody = {
  typedName: string;
  signature: string;
  accepted: true;
};

export enum AuditQuestionScoreState {
  Scored = 'scored',
  Excluded = 'excluded',
  Unscored = 'unscored',
}

// Every level serialises `null` rather than 0 when nothing is scorable, so a real 0 stays a score.
export type AuditScoreQuestion = {
  key: string;
  state: AuditQuestionScoreState;
  earned: number | null;
  max: number | null;
  percentage: number | null;
};

export type AuditScoreSection = {
  key: string;
  earned: number | null;
  max: number | null;
  percentage: number | null;
  questions: AuditScoreQuestion[];
};

export type AuditScore = {
  earned: number | null;
  max: number | null;
  percentage: number | null;
  sections: AuditScoreSection[];
};

export type QuestionActivityStats = {
  comments: number;
  files: number;
  tasks: number;
  links: number;
};

export type QuestionActivityStatsResponse = Record<string, QuestionActivityStats>;

export type QuestionLinkEntity = {
  uuid: string;
  createdAt: string;
  name: string;
  entityType: string;
};

export type QuestionLink = {
  uuid: string;
  created: string;
  questionId: string;
  creator: UuidableName | null;
  entity: QuestionLinkEntity;
};

export type QuestionTaskLinkTask = {
  uuid: string;
  name: string;
  status: TaskStatus;
  dueDate: string | null;
  assigned: UuidableName | null;
};

export type QuestionTaskLink = {
  uuid: string;
  created: string;
  questionId: string;
  creator: UuidableName | null;
  task: QuestionTaskLinkTask;
};

export type QuestionActivityHost = 'draft' | 'submission';

export type AuditSubmissionType = {
  form: EntityFormType;
  content: Record<string, AnswerTypes>;
  // The sections the auditor visited, in order; `content` is pruned to these at submit.
  stack: string[];
  imported: boolean;
};

/** `links[].entity` discriminator a task carries when it is linked to an audit. */
export const AuditLinkEntity = 'audit';

export type AuditRecordDetailsType = EntityDetailsType & {
  // Absent until submit. Only `legacySubmission` means a migrated audit — a third state.
  submission: AuditSubmissionType | null;
  legacySubmission: unknown | null;
  score: AuditScore | null;
  // Bare string here; the `audits__auditStatus` field value is a one-item list.
  auditStatus: AuditStatus;
  // Null once submitted, and also on a scheduled audit started but never opened — that one has no
  // draft *yet*, so branch on `submission` to tell the two apart.
  draft: { uuid: string; form: { uuid: string; name: string } } | null;
};

export type SubmissionVersionAttribution = 'partner' | 'user' | 'unknown';

export type SubmissionVersionType = {
  uuid: string;
  version: number;
  isCurrent: boolean;
  isFirst: boolean;
  /** When this version became current. */
  currentFrom: string;
  supersededAt: string | null;
  attribution: SubmissionVersionAttribution;
  source: string | null;
  /** Non-null only when `attribution` is `user`. */
  changedBy: UuidableName | null;
  content: Record<string, AnswerTypes>;
  absentKeys: string[];
};

export type AmendmentSummaryType = {
  amendmentCount: number;
  currentVersion: number;
  sources: string[];
  lastAmendedAt: string;
};

export type LibraryEntityType = EntityModules.Controls | EntityModules.Hazards;

export type LibraryArchiveResponse = { uuid: string; archivedAt: string | null };

export type LibraryUsedBy = { uuid: string; name: string };

export type LibraryCategory = {
  key: string;
  name: string;
  usedByCount: number;
  usedBy: LibraryUsedBy[];
};

export type LibraryCategoryList = { results: LibraryCategory[] };

export type RiskControlOption = {
  uuid: string;
  id: number | null;
  name: string;
  description: string | null;
  category: { key: string; name: string } | null;
  status: ControlStatus;
  owner: UuidableName | null;
  /** Epoch milliseconds, only while Proposed. */
  dueDate: number | null;
};

export type RiskControlOptionsParams = { search?: string; limit?: number; offset?: number };

export type RiskControlOptionsResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: RiskControlOption[];
};

export enum RiskControlEventType {
  Added = 'added',
  Removed = 'removed',
  StatusChanged = 'statusChanged',
  OwnerChanged = 'ownerChanged',
  DueDateChanged = 'dueDateChanged',
  EvidenceSubmitted = 'evidenceSubmitted',
}

type RiskControlEventBase = { control: { uuid: string | null; name: string } };

/** A risk History row's `current` for the synthetic `riskControlEvent` key. Names and labels are as they were. */
export type RiskControlEventValue = RiskControlEventBase &
  (
    | { event: RiskControlEventType.Added; status: string; owner: string | null; dueDate?: number | null }
    | { event: RiskControlEventType.Removed }
    | { event: RiskControlEventType.StatusChanged; from: string; to: string }
    | { event: RiskControlEventType.OwnerChanged; from: string | null; to: string | null }
    | { event: RiskControlEventType.DueDateChanged; from: number | null; to: number | null }
    | { event: RiskControlEventType.EvidenceSubmitted; status?: string; files?: string[] }
  );
